/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import { formatRupiah, isEmptyPrice } from '../../../../../utils/helper';
import TooltipInfo from '../../../../../components/TooltipInfo';
import { TicketDiscountIcon } from '../../../../../utils/svg';
import { useSelector } from 'react-redux';

const PaymentSummary = () => {
	const { detail } = useSelector((store) => store?.order);
	const data = detail?.request_post_datas;

	const tooltipOtherFee = useMemo(() => {
		const result = [];
		if (!isEmptyPrice(data?.onlineFee)) {
			result?.push({
				name: 'Online Fee',
				desc: "Biaya pemeliharaan sistem dan layanan aplikasi untuk mendukung kelancaran operasional setiap pesanan Anda.",
				total: null,
				nominal: data?.onlineFee,
			});
		}
		if (!isEmptyPrice(data?.packagingFee)) {
			result?.push({
				name: 'Packaging Fee',
				desc: 'Biaya untuk kemasan makanan dan minuman agar tetap aman, rapi, dan higienis selama proses pengemasan.',
				total: null,
				nominal: data?.packagingFee,
			});
		}
		if (!isEmptyPrice(data?.platformFee)) {
			result?.push({
				name: 'Platform Fee',
				desc: 'Biaya layanan untuk mendukung operasional platform, termasuk pemprosesan pesanan dan pengembangan fitur agar pengalaman pemesanan tetap lancar.',
				total: null,
				nominal: data?.platformFee,
			});
		}
		if (!isEmptyPrice(data?.paymentFee)) {
			result?.push({
				name: 'Payment Fee',
				desc: 'Biaya untuk memproses pembayaran secara aman melalui sistem pembayaran, termasuk biaya dari penyedia layanan pembayaran.',
				total: null,
				nominal: data?.paymentFee,
			});
		}
		return result;
	}, [data]);

	const otherFee = useMemo(() => {
		return Number(data?.packagingFee ?? 0) + Number(data?.platformFee ?? 0) + Number(data?.paymentFee ?? 0);
	}, [data]);

	const totalB2BVoucherNominal = (voucherList = []) => {
		if (!voucherList.length) return 0;
		return voucherList.reduce((total, voucher) => total + voucher.voucher_nominal, 0);
	};

	const totalNominalB2BVoucher = useMemo(() => {
		return totalB2BVoucherNominal(data?.b2b_voucher) || 0;
	}, [data]);

	const totalB2BVoucherPrice = (voucherList) => {
		if (!voucherList?.length) return 0;
		return voucherList.reduce((total, voucher) => total + voucher.voucher_price_user, 0);
	};

	const totalPriceB2BVoucher = useMemo(() => {
		return totalB2BVoucherPrice(data?.b2b_voucher) || 0;
	}, [data]);

	const totalMyVoucherNominal = (voucherList) => {
		if (!voucherList?.length) return 0;
		return voucherList.reduce((total, voucher) => total + voucher?.nominal_voucher, 0);
	};

	const totalGlobalVoucherNominal = (voucherList) => {
		if (!voucherList?.length) return 0;
		return voucherList.reduce((total, voucher) => total + voucher?.nominal_voucher, 0);
	};

	const maxNominalVoucher = useMemo(() => {
		const totalMyVoucher = totalMyVoucherNominal(data?.my_voucher) || 0;
		const totalB2BVoucher = totalB2BVoucherNominal(data?.b2b_voucher) || 0;
		const totalGlobalVoucher = totalGlobalVoucherNominal(data?.voucher_global) || 0;
		return totalMyVoucher + totalGlobalVoucher + totalB2BVoucher;
	}, [data]);

	const groupMyForTooltip = (myVoucherList = []) => {
		if (!myVoucherList?.length) return [];

		const result = [];
		myVoucherList?.forEach((voucher) => {
			const existingVoucher = result.find((v) => v.nominal === voucher.nominal_voucher);
			if (existingVoucher) {
				existingVoucher.total += 1;
			} else {
				result.push({
					name: voucher?.name,
					desc: 'Using the available voucher in my voucher.',
					total: 1,
					nominal: voucher?.nominal_voucher,
				});
			}
		});

		return result;
	};

	const groupB2BForTooltip = (b2bVoucherList = []) => {
		if (!b2bVoucherList?.length) return [];

		const result = [];
		b2bVoucherList?.forEach((voucher) => {
			const existingVoucher = result.find((v) => v.nominal === voucher.voucher_nominal);

			if (existingVoucher) {
				existingVoucher.total += voucher?.voucher_qty;
			} else {
				result.push({
					name: voucher?.voucher_name,
					desc: 'Purchase voucher available at related merchants.',
					total: 1,
					nominal: voucher?.voucher_nominal,
				});
			}
		});

		return result;
	};

	const groupVGForTooltip = (voucherGlobalList = []) => {
		if (!voucherGlobalList?.length) return [];

		const listUseVoucherGlobal = voucherGlobalList?.filter((voucher) => voucher.is_use) || [];

		const result = [];

		listUseVoucherGlobal?.forEach((voucher) => {
			const existingVoucher = result.find((v) => v.code === voucher.code);

			if (existingVoucher) {
				existingVoucher.total += 1;
			} else {
				result.push({
					name: voucher?.code,
					desc: 'Using the available voucher in voucher global.',
					total: voucher?.total ?? 1,
					nominal: voucher?.nominal_voucher,
				});
			}
		});

		return result;
	};

	const tooltipVoucherList = useMemo(() => {
		const groupMyVoucher = groupMyForTooltip(data?.my_voucher);
		const groupB2BVoucher = groupB2BForTooltip(data?.b2b_voucher);
		const groupVGVoucher = groupVGForTooltip(data?.voucher_global);
		return [...groupMyVoucher, ...groupB2BVoucher, ...groupVGVoucher];
	}, [data]);

	const strikeOutTotalPayment = useMemo(() => {
		return Number(data?.subTotal) + Number(otherFee) + Number(data?.deliveryFee) + Number(data?.tax) + Number(data?.pb1);
	}, [data, otherFee]);

	return (
		<div className='my-5'>
			<h3 className='text-base font-semibold text-gray-800'>Rincian Pembayaran</h3>
			<div className='mt-4 border-1 border-solid border-gray-300 px-3 py-4 rounded-lg'>
				<div>
					<div className={`flex justify-between items-center`}>
						<div className='text-gray-900 text-xs font-normal'>F&B Price</div>
						<div className='text-xs font-normal flex'>
							<div className='text-gray-700 pl-2'>{formatRupiah(data?.subTotal || 0)}</div>
						</div>
					</div>
					<div className={`${!data?.tax ? 'hidden' : 'flex'} justify-between items-center mt-1`}>
						<div className='text-gray-900 text-xs font-normal'>Tax</div>
						<div className='text-xs font-normal flex'>
							<div className='text-gray-700 pl-2'>{formatRupiah(data?.tax ?? 0)}</div>
						</div>
					</div>
					<div className={`${!data?.pb1 ? 'hidden' : 'flex'} justify-between items-center mt-1`}>
						<div className='text-gray-900 text-xs font-normal'>PB1</div>
						<div className='text-xs font-normal flex'>
							<div className='text-gray-700 pl-2'>{formatRupiah(data?.pb1 ?? 0)}</div>
						</div>
					</div>
					<div className={`${!data?.additionalDeliveryCharge && !data?.deliveryPartner?.amount} flex justify-between items-center mt-1`}>
						<div className='text-gray-900 text-xs font-normal'>Pengiriman</div>
						<div className='text-xs font-normal flex'>
							<div className='text-gray-700 pl-2'>{formatRupiah(Number(data?.additionalDeliveryCharge ?? 0) + Number(data?.deliveryPartner?.amount ?? 0))}</div>
						</div>
					</div>
					<div className={`${!otherFee ? 'hidden' : 'flex'} justify-between items-center mt-1`}>
						<div className='text-gray-900 text-xs font-normal flex items-center'>
							Biaya lainnya
							<TooltipInfo title='Apa saja biaya di dalam nya?' items={tooltipOtherFee} />
						</div>
						<div className='text-xs font-normal flex'>
							<div className='text-gray-700 pl-2'>{formatRupiah(otherFee ?? 0)}</div>
						</div>
					</div>
					<div className={`${!totalNominalB2BVoucher ? 'hidden' : 'flex'} justify-between items-center mt-1`}>
						<div className='text-gray-900 text-xs font-normal'>Beli Voucher</div>
						<div className='text-xs font-normal flex'>
							<div className='text-gray-400 font-thin line-through'>{formatRupiah(totalNominalB2BVoucher)}</div>
							<div className='text-gray-700 pl-2'>{formatRupiah(totalPriceB2BVoucher)}</div>
						</div>
					</div>
					<div className={`${!maxNominalVoucher ? 'hidden' : 'flex'} justify-between items-center mt-2`}>
						<div className='text-gray-900 text-xs font-normal flex items-center'>
							<TicketDiscountIcon className='h-6 w-6 mr-1' />
							Voucher Terpakai
							<TooltipInfo title='Voucher Terpakai' items={tooltipVoucherList} />
						</div>
						<div className='text-xs font-normal flex'>
							<div className='text-[var(--rose-violet)] pl-2'>-{formatRupiah(maxNominalVoucher || 0)}</div>
						</div>
					</div>
					<div className='border-b-1 border-solid pb-2 border-gray-300' />
					<div className='flex justify-between items-center mt-2'>
						<div className='text-gray-900 text-sm font-extrabold items-center'>Total Pembayaran</div>
						<div className='text-sm font-extrabold flex'>
							<div className={`${!maxNominalVoucher && 'hidden'} text-gray-400 font-thin line-through`}>{formatRupiah(strikeOutTotalPayment)}</div>
							<div className='text-gray-900 pl-2'>{formatRupiah(detail?.totalAmount || 0)}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PaymentSummary;
